import BaseStore from '../BaseStore';

class IndexedDbStore extends BaseStore {
  constructor(name, version, db) {
    super(name, version);
    this.db = db;
  }

  makeTransaction(mode) {
    const tx = this.db.transaction(this.name, mode);
    tx.onerror = (err) => {
      console.warn('Failed to create a transaction', this.name, err);
      console.error(err);
      throw err;
    };
    tx.onblocked = () => console.warn('Could not write. DB was blocked.');
    return tx;
  }

  set(key, data, expiresAt) {
    try {
      const tx = this.makeTransaction('readwrite');
      tx.onabort = (event) => {
        const { error } = event.target; // DOMException
        if (error.name === 'QuotaExceededError') {
          this.debugMsg('🚮 QuotaExceededError encountered', event);
        }
        console.warn(
          'Two processes attempted to write data at the same time and were not batched. This is not a concern if ' +
            'multiple tabs/windows are open. If not this should be reported.',
        );
        throw error;
      };
      tx.onblocked = () => console.warn(`Could not write ${key}. IndexedDB was blocked.`);
      const txStore = tx.objectStore(this.name);
      txStore.add({ key, data, expiresAt });
      this.debugMsg('IdxDB 🖊️ added', key);
    } catch (err) {
      // No need to break the site for a cache. Just warn and fall through
      console.error('Failed to set a resource from indexedDB', key);
    }
  }

  // This is a put operation so the entire object will be replaced.
  update(key, data, expiresAt) {
    try {
      const tx = this.makeTransaction('readwrite');
      const txStore = tx.objectStore(this.name);
      txStore.put({ key, data, expiresAt });
      this.debugMsg('IdxDB 🖊️ updated', key);
    } catch (err) {
      // No need to break the site for a cache. Just warn and fall through
      console.error('Failed to update a resource from indexedDB', key);
    }
  }

  get(key) {
    return new Promise((resolve, reject) => {
      try {
        const tx = this.makeTransaction('readonly');
        tx.onerror = (err) => reject(err);
        const txStore = tx.objectStore(this.name);
        const result = txStore.get(key);
        result.onsuccess = (event) => {
          this.debugMsg('IdxDB 🔎', key, event.target.result);
          resolve(event.target.result);
        };
      } catch (err) {
        console.error('Failed to get a resource from indexedDB', key);
        reject(err);
      }
    });
  }

  async upsert(key, data, expiresAt) {
    const result = await this.get(key);
    if (result === undefined) {
      this.set(key, data, expiresAt);
    } else {
      this.update(key, data, expiresAt);
    }
  }

  delete(key) {
    return new Promise((resolve, reject) => {
      const tx = this.makeTransaction('readwrite');
      tx.onerror = (err) => {
        console.error('Failed to delete a resource from indexedDB', key);
        return reject(err);
      };
      const txStore = tx.objectStore(this.name);
      const result = txStore.delete(key);
      result.onsuccess = () => {
        this.debugMsg('IdxDB ❌ deleted', key);
        resolve();
      };
    });
  }

  deleteStartsWith(substring) {
    return new Promise((resolve, reject) => {
      const tx = this.makeTransaction('readwrite');
      tx.onerror = (err) => reject(err);
      const txStore = tx.objectStore(this.name);
      const range = IDBKeyRange.bound(substring, `${substring}|`, true, true);
      const request = txStore.delete(range);
      request.onsuccess = () => {
        this.debugMsg('IdxDB ❌ deleted all records starting with', substring);
        resolve();
      };
    });
  }

  clear() {
    const tx = this.makeTransaction('readwrite');
    const txStore = tx.objectStore(this.name);
    txStore.clear();
    this.debugMsg('IdxDB ❌ All data deleted.');
  }
}

export default IndexedDbStore;
